import * as React from "react";

import { getArea, getLength } from "ol/sphere";
import Map from "ol/Map";
import OlBaseLayer from "ol/layer/Base";
import Geometry from "ol/geom/Geometry";
import { Point } from "ol/geom";
import LayerGroup from "ol/layer/Group";
import Control from "ol/control/Control";
import Interaction from "ol/interaction/Interaction";
import OlBaseEvent from "ol/events/Event";
import OlCollection from "ol/Collection";


import { MapContextType } from "@/@types/context/MapContext";
import { FunctionComponent, ReactElement, ReactNode } from "react";
import { transform } from "ol/proj";

export {
  // flatLayersCollection,
  // flatLayersArray,
  formatLength,
  formatArea,
  formatPoint,
  flattenLayers,
  findLayer,
  findChild,
  getOnlyGroupLayers,
  getCallDirectLayers,
  getCallGroupedLayers,

  getEvents,
  getDefinedOptions,

  registerOlControl,
  findControl,

  registerOlInteraction,
  findInteraction,

  calcAngle,

  padExtent,

  prepareCoordinatesForDisplay
};

const idKey = "id";

function findLayer(map: Map, id: any): OlBaseLayer | null {

  let foundLayer = null;

  if (map) {
    const mapRootLayers = map.getLayers().getArray();

    //find layer in root level
    const mapLayer = mapRootLayers.find(x => x.get(idKey) === id);
    if (mapLayer) {
      foundLayer = mapLayer;
      return foundLayer;
    }

    //check in GroupLayers
    mapRootLayers.filter(x => x instanceof LayerGroup).forEach(groupLayer => {
      const layer = _findInTree(groupLayer, id);
      if (layer) {
        foundLayer = layer;
      }
    });
  }

  return foundLayer;
}

function _findInTree(topLayer: any, id: string): OlBaseLayer | null | undefined {
  if (topLayer instanceof LayerGroup) {
    const childLayers = topLayer.getLayers().getArray();
    const l = childLayers.find(x => x.get(idKey) === id);
    if (l) {
      return l;
    } else {
      return childLayers
        .filter(x => x instanceof LayerGroup)
        .map(x => _findInTree(x, id))
        .find(x => x !== null && x !== undefined);
    }
  } else if (topLayer.get(idKey) === id) {
    return topLayer;
  } else {
    return null;
  }
}


// function flatLayersCollection(olLayerCollection, steps) {
//   const arr = olLayerCollection.getArray();
//   return flattenLayers(arr, steps);
// }
//
// function flatLayersArray(layers, steps) {
//   return flattenLayers(layers, steps);
// }

function flattenLayers(arr: any[] | null, d = 1): OlBaseLayer[] | [] {
  if (arr) {
    return d > 0
      ? arr.reduce(
        (acc, val) =>
          acc.concat(
            val instanceof LayerGroup && val.getLayers().getArray().length > 0
              //@ts-ignore TODO:
              ? [val].concat(flattenLayers(val.getLayers().getArray(), d - 1))
              : val
          ),
        []
      )
      : arr.slice();
  } else {
    return [];
  }
}

function flattenControls(arr: Control[] | null, d = 1): Control[] {
  if (arr) {
    return d > 0
    ? arr.reduce(
      (acc, val) =>
      acc.concat(
        //@ts-ignore
        typeof val.getControls === "function"
        //@ts-ignore
        ? [val].concat(flattenControls(val.getControls(), d - 1))
        : val
      ),
      []
    )
    : arr.slice()
  } else {
    return []
  }
}

function formatLength(line: Geometry): string {
  const length = getLength(line);
  if (length > 100) {
    return Math.round((length / 1000) * 100) / 100 + " " + "km";
  } else {
    return Math.round(length * 100) / 100 + " " + "m";
  }
}

function formatArea(polygon: Geometry): string {
  const area = getArea(polygon);
  if (area > 10000) {
    return Math.round((area / 1000000) * 100) / 100 + " " + "km²";
  } else {
    return Math.round(area * 100) / 100 + " " + "m²";
  }
}

function formatPoint(point: Point): string {
  const _pointCoords = point.getCoordinates();
  return _pointCoords[0].toFixed(0).toString() + ", " + _pointCoords[1].toFixed(0).toString();
}

function findControl(map: Map, id: any, Control: any): Control | undefined {
  const flatControls = flattenControls(map.getControls().getArray());
  if (id) {
    return flatControls.find(x => x.get("id") === id);
  } else {
    return flatControls.find(x => x instanceof Control);
  }
}

function getOnlyGroupLayers(arr: OlBaseLayer[])  {
  const res = arr.filter(x => x instanceof LayerGroup) as LayerGroup[];
  return res;
}

function getCallDirectLayers(arr: OlBaseLayer[]) {
  const res = arr.filter(x => !(x instanceof LayerGroup)).filter(x => x.get("call_group") === undefined || x.get("call_group") === null) ;
  return res;
}

function getCallGroupedLayers(arr: OlBaseLayer[]) {
  let dict: {[key: string]: OlBaseLayer[]} = {};

  const sortByZIndex = (a: any, b: any) => (b.getZIndex?.() ?? 0) - (a.getZIndex?.() ?? 0);

  arr.filter(x => !(x instanceof LayerGroup)).forEach(l => {
    const call_group: string = l.get('call_group');
    if (call_group) {
      if (dict.hasOwnProperty(call_group)) {
        dict[call_group] = dict[call_group].concat([l]);
      } else {
        dict[call_group] = [l];
      }
    }
  })

  const res = Object.keys(dict).map(key => ({ call_group: key, layers: dict[key].sort(sortByZIndex) }));
  return res;
}

//Controls
function registerOlControl(context: MapContextType, Control: any, props: any, options: object, events: object): () => void {
  let allOptions = Object.assign(options, props);
  let definedOptions = getDefinedOptions(allOptions);

  let control = new Control(definedOptions);

  if(props.id) {
    control.set("id", props.id)
  }

  if (context.map) {
    const mapControl = findControl(context.map, props.id, Control);
    if (mapControl) {
      context.map.removeControl(mapControl);
      // console.log('control removed', Control);
    }
    context.map.addControl(control);
    // console.log('control added', Control);
  } else {
    context.initOptions.controls.push(control);
  }

  let olEvents = getEvents(events, props);
  for (let eventName in olEvents) {
    //@ts-ignore
    control.on(eventName, olEvents[eventName]);
  }

  return () => {
    if (context.map) {
      const mapControl = findControl(context.map, props.id, Control);
      if (mapControl) {
        context.map.removeControl(mapControl);
      }
    }
  }
}

function getDefinedOptions(props: object): object {
  let options = {};
  for(let key in props) {
    if (
      key !== 'children'
      //@ts-ignore
      && typeof props[key] !== 'undefined' //exclude undefined ones
      && !key.match(/^on[A-Z]/)     //exclude events
    ) {
      //@ts-ignore
      options[key] = props[key];
    }
  }
  return options;
}

function getPropsKey(eventName: string): string {
  return 'on' + eventName
    .replace(/(\:[a-z])/g, $1 => $1.toUpperCase())
    .replace(/^[a-z]/, $1 => $1.toUpperCase())
    .replace(':','')
}

function getEvents(events: object = {}, props: object = {}): {[key: string]: OlBaseEvent} | null {
  let prop2EventMap = {};
  for(let key in events) {
    //@ts-ignore
    prop2EventMap[getPropsKey(key)] = key;
  }

  let ret = null;
  for(let propName in props) {
    //@ts-ignore
    let eventName = prop2EventMap[propName];
    //@ts-ignore
    let prop = props[propName];
    if (typeof prop !== 'undefined' && propName.match(/^on[A-Z]/) && eventName) {
      if (ret === null) {
        ret = {}
      }
      //@ts-ignore
      ret[eventName] = prop;
    }
  }

  return ret;
}

// let typeOf = function(obj){
//   return ({}).toString.call(obj)
//     .match(/\s([a-zA-Z]+)/)[1].toLowerCase();
// };
//
// function cloneObject(obj){
//   var type = typeOf(obj);
//   if (type == 'object' || type == 'array') {
//     if (obj.clone) {
//       return obj.clone();
//     }
//     var clone = type == 'array' ? [] : {};
//     for (var key in obj) {
//       clone[key] = cloneObject(obj[key]);
//     }
//     return clone;
//   }
//   return obj;
// }

function findChild(children: ReactNode, childType: FunctionComponent<any>): ReactElement | null | {} {
  let found = null;
  let childrenArr = React.Children.toArray(children);
  for (let i=0; i<childrenArr.length; i++) {
    let child = childrenArr[i];
    //@ts-ignore TODO: No type attribute on child
    if (child.type == childType){
      found = child;
      break;
    }
  }
  return found;
}

//Interactions
function findInteraction(map: Map, Interaction: any): Interaction | undefined {
  return map
    .getInteractions()
    .getArray()
    .find(x => x instanceof Interaction);
}

function registerOlInteraction(context: MapContextType, Interaction: any, props: any, options: object, events: object): () => void {
  let allOptions = Object.assign(options, props);
  let definedOptions = getDefinedOptions(allOptions);

  let interaction = new Interaction(definedOptions);
  if (context.map) {
    const mapInteraction = findInteraction(context.map, Interaction);
    if (mapInteraction) {
      context.map.removeInteraction(mapInteraction);
    }
    context.map.addInteraction(interaction);
  } else {
    context.initOptions.interactions.push(interaction);
  }

  let olEvents = getEvents(events, props);
  for (let eventName in olEvents) {
    //@ts-ignore TODO: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'object | {}'
    interaction.on(eventName, olEvents[eventName]);
  }

  return () => {
    //happens on umount
    if (context.map) {
      const mapInteraction = findInteraction(context.map, Interaction);
      if (mapInteraction) {
        context.map.removeInteraction(mapInteraction);
      }
    }
  };
}

function calcAngle( p1: [number, number], p2: [number, number]) {
  const x = p2[0] - p1[0];
  const y = p2[1] - p1[1];

  return Math.atan2(x, y);
  //reutrn Math.atan2(x, y) * 180 / Math.PI;
}


// padding is percentage as in 10%
function padExtent(extent: number[] | undefined, padding: number = 10): number[] {
  if (!extent || extent.length === 0) {
    return [];
  }
  if (extent.length !== 4) {
    console.error("Expected extent to be length 4 but got: " + extent.length)
  }
  padding = padding / 100;
  const dx = extent[2] - extent[0];
  const dy = extent[3] - extent[1];

  const paddedExtent = new Array(4).fill(0);
  paddedExtent[0] = extent[0] - dx * padding;
  paddedExtent[1] = extent[1] - dy * padding;
  paddedExtent[2] = extent[2] + dx * padding;
  paddedExtent[3] = extent[3] + dy * padding;
  return paddedExtent;
}

// Give coordinates in EPSG:3857, receive coordinates in deg°min'sec" as array with 2 elements
function prepareCoordinatesForDisplay(coordinates: number[]) {
  const coordinatesTransformed = transform(
    coordinates,
    "EPSG:3857",
    "EPSG:4326"
  ); //EPSG:4326

  const coordinatesInDecimal = [
    parseFloat(parseFloat("" + coordinatesTransformed[0]).toFixed(7)),
    parseFloat(parseFloat("" + coordinatesTransformed[1]).toFixed(7))
  ];
  const coordinatesInDegMinSec = [
    ConvertDDToDMS(coordinatesInDecimal[0]),
    ConvertDDToDMS(coordinatesInDecimal[1])
  ];

  function ConvertDDToDMS(dd: number) {
    var deg = dd | 0; // truncate dd to get degrees
    var frac = Math.abs(dd - deg); // get fractional part
    var min = (frac * 60) | 0; // multiply fraction by 60 and truncate
    var sec = parseFloat("" + (frac * 3600 - min * 60)).toFixed(2);

    return deg + "°" + min + "'" + sec + "\"";
  }

  return coordinatesInDegMinSec;
}